import React, { useEffect, useRef, useState } from 'react';
import {
  PRODUCT_SELECTOR_DEBOUNCE_MS,
  PRODUCT_SELECTOR_MIN_QUERY_LENGTH,
  PRODUCT_SELECTOR_RESULT_PAGE_SIZE,
} from '../constants';
import { getProductTotalsAbortable } from '../hooks/useProducts';

type Props = {
  organizationId?: number;
  productName?: string;
  onSelect?: (item: ProductMatch) => void;
  onClear?: () => void;
  mappedValue: string | undefined;
};

type ProductMatch = {
  id?: number | string;
  productId?: number | string;
  name?: string;
  productName?: string;
  brandName?: string;
};

const DROPDOWN_HEIGHT = 220;

const LinkProductSelector = ({
  productName = '',
  organizationId,
  onSelect,
  onClear,
  mappedValue = undefined,
}: Props) => {
  const [query, setQuery] = useState(mappedValue || '');
  const [results, setResults] = useState<ProductMatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [openAbove, setOpenAbove] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollParentRef = useRef<HTMLElement | null>(null);
  const inFlightRef = useRef<ReturnType<typeof getProductTotalsAbortable> | null>(null);
  const debounceRef = useRef<number | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    setQuery(mappedValue || '');
  }, [mappedValue]);

  const findScrollParent = (element: HTMLElement | null): HTMLElement | null => {
    let parent = element?.parentElement;
    while (parent) {
      const style = window.getComputedStyle(parent);
      const overflowY = style.overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  };

  const checkDropdownPosition = () => {
    if (!containerRef.current) return;

    const inputRect = containerRef.current.getBoundingClientRect();
    const scrollParent = scrollParentRef.current;

    const containerBottom = scrollParent
      ? scrollParent.getBoundingClientRect().bottom
      : window.innerHeight;

    const spaceBelow = containerBottom - inputRect.bottom;
    const spaceAbove =
      inputRect.top - (scrollParent ? scrollParent.getBoundingClientRect().top : 0);

    setOpenAbove(spaceBelow < DROPDOWN_HEIGHT + 10 && spaceAbove > spaceBelow);
  };

  const isDropdownVisible = loading || !!loadError || (hasSearched && query.trim());

  useEffect(() => {
    if (!isDropdownVisible || !containerRef.current) return;

    scrollParentRef.current = findScrollParent(containerRef.current);
    const scrollParent = scrollParentRef.current;

    checkDropdownPosition();

    if (scrollParent) {
      scrollParent.addEventListener('scroll', checkDropdownPosition);
      return () => {
        scrollParent.removeEventListener('scroll', checkDropdownPosition);
      };
    }
  }, [isDropdownVisible]);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    if (mappedValue) {
      if (inFlightRef.current) inFlightRef.current.cancel();
      setResults([]);
      setLoading(false);
      setLoadError(null);
      setHasSearched(false);
      return;
    }

    if (!organizationId) {
      if (inFlightRef.current) inFlightRef.current.cancel();
      setResults([]);
      setLoading(false);
      setLoadError(null);
      setHasSearched(false);
      return;
    }

    const trimmed = query.trim();
    if (!trimmed || trimmed.length < PRODUCT_SELECTOR_MIN_QUERY_LENGTH) {
      if (inFlightRef.current) inFlightRef.current.cancel();
      setResults([]);
      setLoading(false);
      setLoadError(null);
      setHasSearched(false);
      return;
    }

    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
    }

    debounceRef.current = window.setTimeout(() => {
      if (inFlightRef.current) inFlightRef.current.cancel();
      setLoading(true);
      setLoadError(null);
      setHasSearched(false);

      const request = getProductTotalsAbortable({
        organizationId,
        name: trimmed,
        page: 0,
        pageSize: PRODUCT_SELECTOR_RESULT_PAGE_SIZE,
        active: true,
      });
      inFlightRef.current = request;

      request.promise
        .then((resp) => {
          if (requestId !== requestIdRef.current) return;
          const responsePayload = resp as any;
          const data = Array.isArray(responsePayload?.data)
            ? responsePayload.data
            : Array.isArray(responsePayload)
              ? responsePayload
              : [];
          setResults(data);
          setHasSearched(true);
        })
        .catch((err) => {
          if (requestId !== requestIdRef.current) return;
          if (
            err?.name === 'AbortError' ||
            err?.name === 'CanceledError' ||
            err?.code === 'ERR_CANCELED'
          ) {
            return;
          }
          console.error('[LinkProductSelector] Failed to fetch product matches:', err);
          setLoadError('Failed to load matches');
          setHasSearched(true);
        })
        .finally(() => {
          if (requestId === requestIdRef.current && inFlightRef.current === request) {
            setLoading(false);
          }
        });
    }, PRODUCT_SELECTOR_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
      if (inFlightRef.current) {
        inFlightRef.current.cancel();
      }
      if (requestIdRef.current === requestId) {
        requestIdRef.current += 1;
      }
    };
  }, [mappedValue, organizationId, query]);

  const getResultLabel = (item: ProductMatch) => {
    return String(item?.name || item?.productName || 'Untitled product');
  };

  const handleSelect = (item: ProductMatch) => {
    setQuery(getResultLabel(item));
    setResults([]);
    if (onSelect) {
      onSelect(item);
    }
  };

  const handleClear = () => {
    if (!onClear) return;
    if (inFlightRef.current) {
      inFlightRef.current.cancel();
    }
    setQuery('');
    setResults([]);
    setLoading(false);
    setLoadError(null);
    setHasSearched(false);
    onClear();
  };

  return (
    <div ref={containerRef} style={{ minWidth: 0, position: 'relative' }} data-testid="link-product-selector">
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          className="input"
          type="text"
          placeholder="Map to existing product"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{
            width: '100%',
            minWidth: 0,
            cursor: mappedValue ? 'not-allowed' : 'pointer',
          }}
          aria-label={`Map ${productName} to existing product`}
          autoComplete="off"
          disabled={!!mappedValue}
          data-testid="link-product-selector-input"
        />
        {mappedValue && onClear && (
          <button
            type="button"
            className="button secondary"
            onClick={handleClear}
            aria-label={`Clear mapped product for ${productName || 'product'}`}
            data-testid="link-product-selector-clear"
          >
            Clear
          </button>
        )}
      </div>

      {isDropdownVisible && (
        <div
          className="card"
          data-testid="link-product-selector-results"
          style={{
            position: 'absolute',
            ...(openAbove ? { bottom: '100%', marginBottom: 6 } : { top: '100%', marginTop: 6 }),
            left: 0,
            right: 0,
            zIndex: 20,
            padding: 6,
            maxHeight: DROPDOWN_HEIGHT,
            overflowY: 'auto',
          }}
        >
          {loading && (
            <div className="muted" style={{ fontSize: 12, padding: 6 }} data-testid="link-product-selector-loading">
              Searching...
            </div>
          )}
          {!loading && loadError && (
            <div className="muted" style={{ fontSize: 12, padding: 6 }} data-testid="link-product-selector-error">
              {loadError}
            </div>
          )}
          {!loading && !loadError && results.length === 0 && (
            <div className="muted" style={{ fontSize: 12, padding: 6 }} data-testid="link-product-selector-empty">
              No matches
            </div>
          )}
          {!loading &&
            !loadError &&
            results.map((item, index) => {
              const key = String(item?.id ?? item?.productId ?? `${getResultLabel(item)}-${index}`);
              return (
                <button
                  key={key}
                  type="button"
                  onMouseDown={() => handleSelect(item)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    border: 'none',
                    background: 'transparent',
                    padding: '6px 8px',
                    borderRadius: 6,
                    cursor: 'pointer',
                  }}
                  aria-label={`Select ${getResultLabel(item)}`}
                  data-testid={`link-product-selector-option-${key}`}
                >
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{getResultLabel(item)}</div>
                  {item?.brandName && (
                    <div className="muted" style={{ fontSize: 11 }}>
                      {item.brandName}
                    </div>
                  )}
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default LinkProductSelector;
